// ─────────────────────────────────────────────
// Shared / Primitives
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// Media
// ─────────────────────────────────────────────

export interface DocumentCollectionItemSummary {
  id: number
  displayName: string
  systemFileName: string
  mimeType: string
  description?: string | null
}

export interface GetCollectionByTagResponse {
  name: string
  description?: string | null
  items: DocumentCollectionItemSummary[]
}

export interface DocumentCollectionItemBundleItem {
  id: number
  displayName: string
  systemFileName: string
  mimeType: string
  description?: string | null
}

export interface DocumentCollectionBundleItem {
  id: number
  tag: string
  name: string
  description?: string | null
  items: DocumentCollectionItemBundleItem[]
}


/** ISO 8601 UTC datetime string, e.g. "2026-04-21T14:30:00Z" */
export type IsoDateString = string

/** ModelCategory enum — mirrors CmmiViewer.Domain.Enums.ModelCategory */
export const ModelCategory = {
  Domain: 0,
  CapabilityArea: 1,
  Mddap: 2,
  Aim: 3,
} as const
export type ModelCategory = (typeof ModelCategory)[keyof typeof ModelCategory]

/** LevelType enum — mirrors CmmiViewer.Domain.Enums.LevelType */
export const LevelType = {
  Numeric: 0,
  Mddap: 1,
} as const
export type LevelType = (typeof LevelType)[keyof typeof LevelType]

/** ContentType enum values from the API */
export type ContentType =
  | 'ModelView'
  | 'PracticeArea'
  | 'Practice'
  | 'ContextualContent'

// ─────────────────────────────────────────────
// Current User
// ─────────────────────────────────────────────

export interface ApiRole {
  id: number
  name: string
}

export interface GetCurrentUserResponse {
  userId: number
  /** Null only for a record that has not yet been claimed by a login — never null for the caller. */
  remoteId: string | null
  /** SAML username, normally an email address. Its domain gates custom view sharing. */
  username: string | null
  roles: ApiRole[]
  /**
   * The content language the SPA should use — the user's optional stored preference (when active)
   * coalesced with the system-wide default language. Never null. Adopted on session load.
   */
  preferredLanguage: string
  /**
   * True when the user has explicitly chosen a content language that still maps to an active
   * language. When false, `preferredLanguage` is only the system default and the SPA prompts for
   * a language on load (provided more than one language is available).
   */
  hasLanguagePreference: boolean
  lastLoginAt: IsoDateString | null
  /**
   * Stable hashed identifier for this user (same value stamped onto server request telemetry).
   * Attached to App Insights page-view telemetry so client and server telemetry correlate.
   */
  hashedUserId: string
  /**
   * Codes of the active languages the user can draft-preview: a role of theirs appears in some
   * active (Open/Locked) bundle's PreviewRoleIds and the global DraftPreviewEnabled setting is on.
   * The "Show Draft Content" toggle is gated on the selected language being in here, so publishing
   * the bundle, revoking the role or turning the setting off takes it away again.
   */
  draftPreviewLanguages: string[]
  /** The user holds a role with CanSubmitFeedback. Combine with the bundle's feedbackEnabled. */
  canSubmitFeedback: boolean
}

// ─────────────────────────────────────────────
// Languages
// ─────────────────────────────────────────────

export interface ApiLanguage {
  id: number
  code: string
  name: string
  isDefault: boolean
  /** Hidden language offered only because the user can draft-preview a bundle targeting it. */
  isDraftPreviewOnly: boolean
}

export interface GetLanguagesResponse {
  languages: ApiLanguage[]
}

// ─────────────────────────────────────────────
// Content Bundle
// ─────────────────────────────────────────────
import type { TourStepDto } from '@/types/tourSteps'

export interface ApiModelView {
  id: number
  name: string
  acronym: string
  sortOrder: number
  category: ModelCategory
  displayName: string
  description: string | null
  fallback: boolean
  /** True when content is sourced from a Draft/InReview translation overlay (draft preview mode). */
  isDraft: boolean
  /** Draft-preview only: camelCase field name → published (original) value, for fields that differ from the draft. Used to render an inline content diff. */
  originalFields?: Record<string, string | null>
}

export interface ApiLevel {
  id: number
  name: string
  sequence: number
  category: LevelType
  displayName: string
  description: string | null
  fallback: boolean
  /** True when content is sourced from a Draft/InReview translation overlay (draft preview mode). */
  isDraft: boolean
  /** Draft-preview only: camelCase field name → published (original) value, for fields that differ from the draft. Used to render an inline content diff. */
  originalFields?: Record<string, string | null>
}

export interface ApiPracticeArea {
  id: number
  name: string
  acronym: string
  sortOrder: number
  iconUri: string | null
  /** Version of the translation this item was rendered from. Reported with feedback. */
  version: number
  displayName: string
  intent: string | null
  value: string | null
  description: string | null
  additionalRequiredInformation: string | null
  additionalExplanatoryInformation: string | null
  fallback: boolean
  /** True when content is sourced from a Draft/InReview translation overlay (draft preview mode). */
  isDraft: boolean
  /** Draft-preview only: camelCase field name → published (original) value, for fields that differ from the draft. Used to render an inline content diff. */
  originalFields?: Record<string, string | null>
}

export interface ApiPractice {
  id: number
  practiceAreaId: number
  name: string
  sortOrder: number
  /** Version of the translation this item was rendered from. Reported with feedback. */
  version: number
  displayName: string
  statement: string | null
  value: string | null
  description: string | null
  additionalRequiredInformation: string | null
  additionalExplanatoryInformation: string | null
  exampleActivities: string | null
  exampleWorkProducts: string | null
  fallback: boolean
  /** True when content is sourced from a Draft/InReview translation overlay (draft preview mode). */
  isDraft: boolean
  /** Draft-preview only: camelCase field name → published (original) value, for fields that differ from the draft. Used to render an inline content diff. */
  originalFields?: Record<string, string | null>
}

export interface ApiContextualContent {
  id: number
  contentTagId: number
  practiceAreaId: number | null
  practiceId: number | null
  content: string | null
  fallback: boolean
  /** True when content is sourced from a Draft/InReview translation overlay (draft preview mode). */
  isDraft: boolean
  /** Draft-preview only: camelCase field name → published (original) value, for fields that differ from the draft. Used to render an inline content diff. */
  originalFields?: Record<string, string | null>
}

export interface ApiContentTag {
  id: number
  slug: string
  sortOrder: number
  name: string
  displayName: string | null
  description: string | null
}

export interface ApiGlossaryTerm {
  id: number
  name: string
  sortOrder: number
  term: string
  definition: string | null
  /** Glossary sort key: the translation's sort value, or `term` when it has none. Absent in bundles cached before it existed. */
  sortValue?: string
  /** Published default-language term; null when the bundle is in the default language. */
  sourceTerm?: string | null
  fallback: boolean
  isDraft: boolean
  /** Draft-preview only: camelCase field name → published (original) value, for fields that differ from the draft. Used to render an inline content diff. */
  originalFields?: Record<string, string | null>
}

export enum AdditionalReadingCategory {
  Overview     = 0,
  Appendices   = 1,
  ReleaseNotes = 2,
  Faq          = 3,
  Crosswalks   = 4,
}

export interface ApiAdditionalReadingItem {
  id: number
  name: string
  category: AdditionalReadingCategory
  sortOrder: number
  displayName: string
  content: string | null
  fallback: boolean
  isDraft: boolean
  /** Draft-preview only: camelCase field name → published (original) value, for fields that differ from the draft. Used to render an inline content diff. */
  originalFields?: Record<string, string | null>
}

export interface ApiModelViewPracticeArea {
  id: number
  modelViewId: number
  practiceAreaId: number
}

export interface ApiPracticeLevel {
  id: number
  practiceId: number
  levelId: number
}

export interface ApiRelatedPracticeArea {
  id: number
  sourcePracticeAreaId: number
  targetPracticeAreaId: number
}

export interface ApiPracticeRelatedPracticeArea {
  id: number
  practiceId: number
  relatedPracticeAreaId: number
}

export interface ApiExternalFramework {
  id: number
  name: string
  shortCode: string
  description?: string | null
  referenceUrl?: string | null
}

export interface ApiExternalFrameworkClause {
  id: number
  externalFrameworkId: number
  clauseKey: string
  topic: string
  section: number
  parentClauseId?: number | null
}

export interface ApiPracticeCrosswalkMapping {
  id: number
  practiceId: number
  externalFrameworkClauseId: number
  externalFrameworkId: number
  relationshipType: number
  note?: string | null
}

export interface GetContentBundleResponse {
  language: string
  generatedAt: IsoDateString
  contentHash: string
  /** Mirrors the FeedbackEnabled app setting — gates the feedback entry point (works offline). */
  feedbackEnabled: boolean
  /** Mirrors the CrossWalksEnabled app setting; true selects Crosswalks additional reading over the legacy document collection. Absent in bundles cached before it existed. */
  crossWalksEnabled?: boolean
  /** Mirrors the SharedCustomViewsEnabled app setting; gates every sharing entry point (works offline). */
  sharedCustomViewsEnabled: boolean
  /** Mirrors the MediaGalleryEnabled app setting; gates the viewer media gallery (works offline). Absent in bundles cached before it existed, which means disabled. */
  mediaGalleryEnabled?: boolean
  /** The bundle language's glossary highlighting setting. Absent in bundles cached before it existed, which means enabled. */
  glossaryHighlightingEnabled?: boolean
  modelViews: ApiModelView[]
  levels: ApiLevel[]
  practiceAreas: ApiPracticeArea[]
  practices: ApiPractice[]
  contextualContent: ApiContextualContent[]
  contentTags: ApiContentTag[]
  glossaryTerms: ApiGlossaryTerm[]
  additionalReading: ApiAdditionalReadingItem[]
  tourSteps: TourStepDto[]
  customViewTourSteps: TourStepDto[]
  documentCollections: DocumentCollectionBundleItem[]
  modelViewPracticeAreas: ApiModelViewPracticeArea[]
  practiceLevels: ApiPracticeLevel[]
  relatedPracticeAreas: ApiRelatedPracticeArea[]
  practiceRelatedPracticeAreas: ApiPracticeRelatedPracticeArea[]
  externalFrameworks: ApiExternalFramework[]
  externalFrameworkClauses: ApiExternalFrameworkClause[]
  practiceCrosswalkMappings: ApiPracticeCrosswalkMapping[]
  /** Media assets referenced by the bundle, to cache for offline use. Absent in bundles cached before it existed. */
  media?: BundleMediaAsset[]
  /** Absolute external image URLs referenced by the bundle. Absent in bundles cached before it existed. */
  externalMedia?: string[]
}

export interface BundleMediaAsset {
  systemFileName: string
  originalFileName: string
  displayName: string
  mediaTitle?: string | null
  altText?: string | null
  mimeType: string
  fileSize: number
  contentHash?: string | null
  updatedOn?: IsoDateString | null
}

export interface GetContentVersionResponse {
  language: string
  contentHash: string
}

// ─────────────────────────────────────────────
// Search
// ─────────────────────────────────────────────

export type SearchResultContentType =
  | 'PracticeArea'
  | 'Practice'
  | 'ContextualContent'
  | 'GlossaryTerm'
  | 'Overview'
  | 'Appendices'
  | 'ReleaseNotes'
  | 'Faq'
  | 'Crosswalks'
  | 'Framework'

export interface ApiSearchResult {
  contentType: SearchResultContentType
  entityId: number
  title: string
  snippet: string | null
  practiceAreaId: number | null
  practiceAreaName: string | null
  parentPracticeId: number | null
  relevanceScore: number
}

// ─────────────────────────────────────────────
// Notes
// ─────────────────────────────────────────────

export interface ApiNote {
  id: number
  contentType: ContentType
  entityId: number
  title: string | null
  body: string
  updatedOn: IsoDateString | null
}

export interface GetNotesResponse {
  notes: ApiNote[]
}

export type NoteOperation = 'Create' | 'Update' | 'Delete'

export interface NoteChange {
  clientId: string
  id: number | null
  operation: NoteOperation
  contentType?: ContentType
  entityId?: number
  title?: string | null
  body?: string
  clientUpdatedOn: IsoDateString
}

export interface SyncNotesRequest {
  changes: NoteChange[]
}

export interface SyncNotesResponse {
  syncedAt: IsoDateString
  created: Array<{ clientId: string; serverId: number }>
  conflicts: ApiNote[]
  notes: ApiNote[]
}

// ─────────────────────────────────────────────
// Bookmarks
// ─────────────────────────────────────────────

export interface ApiBookmark {
  id: number
  contentType: ContentType
  entityId: number
  createdOn: IsoDateString | null
}

export interface GetBookmarksResponse {
  bookmarks: ApiBookmark[]
}

export interface CreateBookmarkRequest {
  contentType: ContentType
  entityId: number
}

export type BookmarkOperation = 'Create' | 'Delete'

export interface BookmarkChange {
  clientId: string
  /** Server-assigned ID — required for Delete, null for Create */
  id: number | null
  operation: BookmarkOperation
  contentType?: ContentType
  entityId?: number
  /** Negative temp ID assigned client-side for optimistic creates */
  tempId?: number
}

export interface SyncBookmarksRequest {
  changes: BookmarkChange[]
}

export interface SyncBookmarksResponse {
  syncedAt: IsoDateString
  /** Maps clientId → server-assigned bookmark ID for newly created bookmarks */
  created: Array<{ clientId: string; serverId: number }>
  bookmarks: ApiBookmark[]
}

// ─────────────────────────────────────────────
// Custom Views
// ─────────────────────────────────────────────

export interface ApiCustomViewPracticeArea {
  practiceAreaId: number
  levelId: number   // required; always a numeric level
}

export interface ApiCustomView {
  id: number
  name: string
  practiceAreas: ApiCustomViewPracticeArea[]
  // Optional on read: records cached in IndexedDB before sharing shipped lack these, and were always owned.
  isOwner?: boolean
  canEdit?: boolean
  ownerUsername?: string | null
  shareCount?: number
  /** Notes attached to the view itself, visible to everyone it is shared with. */
  noteCount?: number
  updatedOn?: IsoDateString | null
}

/** A cross-domain share the caller has not accepted yet. Carries no view content. */
export interface ApiPendingCustomView {
  id: number
  name: string
  ownerUsername: string | null
  canEdit: boolean
}

export interface GetCustomViewsResponse {
  customViews: ApiCustomView[]
  pendingCustomViews?: ApiPendingCustomView[]
}

export interface CustomViewMutationRequest {
  name: string
  practiceAreas: ApiCustomViewPracticeArea[]
  expectedUpdatedOn?: IsoDateString | null
}

export interface ApiCustomViewShare {
  id: number
  userId: number
  username: string
  isPending: boolean
  canEdit: boolean
  createdOn: IsoDateString | null
}

export interface GetCustomViewSharesResponse {
  shares: ApiCustomViewShare[]
}

/**
 * awaitingAcceptance is true for every cross-domain request, including addresses with no account,
 * so the response never reveals whether a recipient exists.
 */
export interface CreateCustomViewShareResponse {
  share: ApiCustomViewShare | null
  awaitingAcceptance: boolean
}

/** crossDomain is true for every cross-domain request, whether or not the address has an account. */
export interface CopyCustomViewResponse {
  crossDomain: boolean
}

export interface CreateCustomViewShareRequest {
  username: string
  canEdit: boolean
}

export interface UpdateCustomViewShareRequest {
  canEdit: boolean
}

export interface CopyCustomViewRequest {
  username: string
}

export interface ApiCustomViewNote {
  id: number
  contentType: ContentType
  entityId: number
  title: string | null
  body: string
  updatedOn: IsoDateString | null
  updatedBy: string | null
  /** Base64 rowversion. Null when the API runs on a provider without one (unit-test fixtures). */
  rowVersion: string | null
}

export interface GetCustomViewNotesResponse {
  notes: ApiCustomViewNote[]
  canEdit: boolean
  isOwner: boolean
}

export interface UpsertCustomViewNoteRequest {
  contentType: ContentType
  entityId: number
  title?: string | null
  body: string
  /** The token the note was read at. Omit to write unconditionally. */
  expectedRowVersion?: string | null
}

export interface ApiCustomViewNoteVersion {
  title: string | null
  body: string
  updatedBy: string | null
  updatedOn: IsoDateString | null
  validFrom: IsoDateString
  validTo: IsoDateString
}

export interface GetCustomViewNoteHistoryResponse {
  versions: ApiCustomViewNoteVersion[]
  /** False when the store has no system versioning, so the UI hides the affordance. */
  supported: boolean
}

export interface CopyNoteToCustomViewRequest {
  contentType: ContentType
  entityId: number
}

export interface CopyAllNotesToCustomViewResponse {
  copiedCount: number
}

// ─────────────────────────────────────────────
// Feedback
// ─────────────────────────────────────────────

/**
 * Snapshot of what the user was looking at when they submitted feedback.
 * The set of data points is expected to grow — add optional fields.
 */
export interface FeedbackContext {
  languageCode: string
  /** 'Practice' | 'PracticeArea' | 'Framework' | 'Glossary' | 'Search' | 'Home' | … */
  contentContext?: string
  entityId?: number
  contentLabel?: string
  /** Version of the translated entity being read. Absent when the context has no versioned entity. */
  contentVersion?: string
  wasOffline: boolean
  /** True when the entity on screen was rendered from a draft translation overlay (draft preview). */
  wasDraft?: boolean
  modelViewId?: number
  modelViewName?: string
}

/**
 * One feedback entry. The same shape is used for a live submission and for an entry stored
 * verbatim in the feedback-offline-queue store. Identity comes from the session, never the body.
 */
export type FeedbackType = 'SpellingGrammar' | 'Content' | 'AppFunctionality' | 'Other'

export interface FeedbackSubmission {
  /** Client-generated id, echoed back so the queued entry can be cleared. */
  clientId: string
  type: FeedbackType
  comment: string
  /** When the user pressed submit — for queued entries this is the offline moment, not the flush. */
  submittedOn: IsoDateString
  context: FeedbackContext
}

/** One item for a live submission, the whole queue on reconnect. */
export interface SubmitFeedbackRequest {
  items: FeedbackSubmission[]
}

export interface SubmitFeedbackResponse {
  receivedAt: IsoDateString
  accepted: Array<{ clientId: string | null; id: number; submittedOn: IsoDateString }>
}

// ─────────────────────────────────────────────
// Error shapes
// ─────────────────────────────────────────────

export interface ApiValidationError {
  type: 'ValidationError'
  title: string
  errors: Record<string, string[]>
}

export interface ApiServerError {
  type: 'ServerError'
  title: string
  traceId: string
}
